import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { settingsAPI } from '../services/api';

const PrivacyContext = createContext(null);

export const usePrivacy = () => {
  const context = useContext(PrivacyContext);
  if (!context) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
};

export const PrivacyProvider = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  
  // Money visibility toggle (admin only, default hidden for privacy)
  const [showMoney, setShowMoney] = useState(() => {
    const stored = localStorage.getItem('crm_show_money');
    return stored === 'true'; // Default false (hidden)
  });
  
  // Global setting: hide incentives for employees
  const [hideIncentivesForEmployees, setHideIncentivesForEmployees] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Fetch global settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        const settings = response.data?.settings || {};
        setHideIncentivesForEmployees(settings.hide_incentives_for_employees || false);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };
    
    if (user) {
      fetchSettings();
    } else {
      setSettingsLoading(false);
    }
  }, [user]);

  // Persist money toggle to localStorage
  useEffect(() => {
    localStorage.setItem('crm_show_money', showMoney.toString());
  }, [showMoney]);

  const toggleMoneyVisibility = useCallback(() => {
    setShowMoney(prev => !prev);
  }, []);

  // Update the global incentive visibility setting (admin only)
  const updateIncentiveVisibility = useCallback(async (hide) => {
    try {
      await settingsAPI.update({ hide_incentives_for_employees: hide });
      setHideIncentivesForEmployees(hide);
      return { success: true };
    } catch (error) {
      console.error('Failed to update incentive visibility:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Check if current user can see incentives
  const canSeeIncentives = useCallback(() => {
    if (isAdmin) return true; // Admin always sees incentives
    if (hideIncentivesForEmployees) return false; // Setting is ON, employees can't see
    return true; // Setting is OFF, everyone can see
  }, [isAdmin, hideIncentivesForEmployees]);

  // Format money with optional masking
  const formatMoney = useCallback((amount, forceShow = false) => {
    // If forceShow is true or money is visible, show actual value
    if (forceShow || showMoney || !isAdmin) {
      // Non-admins always see real values (unless incentives are hidden entirely)
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(amount || 0);
    }
    // Admin with hidden money shows masked value
    return '₹****';
  }, [showMoney, isAdmin]);

  const value = {
    // Money visibility (admin toggle)
    showMoney,
    toggleMoneyVisibility,
    formatMoney,
    
    // Incentive visibility (global setting)
    hideIncentivesForEmployees,
    updateIncentiveVisibility,
    canSeeIncentives,
    
    // Loading state
    settingsLoading,
    
    // Helper
    isAdmin
  };

  return (
    <PrivacyContext.Provider value={value}>
      {children}
    </PrivacyContext.Provider>
  );
};
